import React, { useState, useEffect } from 'react';
import useSWR from 'swr';
import OnOffToggle from './OnOffToggle';
import { 
  CheckCircle, 
  Clock, 
  Star, 
  ShoppingCart,
  UserPlus,
  AlertTriangle,
  FileText,
  Bell,
  Loader2,
  BarChart3,
  MessageSquare,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import apiV2 from '../utils/apiV2';
import TabPageLayout from './TabPageLayout';

// SWR fetcher
const fetcher = async (url) => {
  const response = await apiV2.get(url);
  return response.data;
};

/**
 * NotificationsTab - Shows email and SMS notification toggles
 * Auto-saves on toggle, preset times
 */
const NotificationsTab = () => {
  const [savingKey, setSavingKey] = useState(null);
  
  // Settings state - includes SMS toggles for high-impact notifications
  const [settings, setSettings] = useState({
    // Customer - Email
    reminderEnabled: true,        // 24h reminder before trip
    thankYouEnabled: true,        // Thank you after trip
    abandonedCartEnabled: true,   // Cart recovery
    // Customer - SMS (high-impact only)
    confirmationSmsEnabled: true, // Booking confirmation via SMS
    reminderSmsEnabled: false,    // 24h reminder via SMS
    cancellationSmsEnabled: false, // Cancellation alerts via SMS
    // Operator
    newBookingEnabled: true,      // New booking alert
    newLeadEnabled: true,         // New lead alert
    weeklyReportEnabled: true,    // Weekly performance report
  });

  // Fetch settings
  const { data: orgData, isLoading, mutate } = useSWR(
    '/auth/organizations/me',
    fetcher,
    { revalidateOnFocus: true, dedupingInterval: 30000 }
  );

  // Load settings from org data
  useEffect(() => {
    // Try to get notification settings from multiple possible locations
    const ns = orgData?.settings?.notificationSettings || orgData?.notificationSettings || {};
    if (Object.keys(ns).length > 0 || orgData) {
      setSettings({
        reminderEnabled: ns.reminderEmailEnabled ?? true,
        thankYouEnabled: ns.thankYouEmailEnabled ?? true,
        abandonedCartEnabled: ns.abandonedCartEmailEnabled ?? true,
        confirmationSmsEnabled: ns.confirmationSmsEnabled ?? true,
        reminderSmsEnabled: ns.reminderSmsEnabled ?? false,
        cancellationSmsEnabled: ns.cancellationSmsEnabled ?? false,
        newBookingEnabled: ns.newBookingEmailEnabled ?? true,
        newLeadEnabled: ns.newLeadEmailEnabled ?? true,
        weeklyReportEnabled: ns.weeklyReportEmailEnabled ?? true,
      });
    }
  }, [orgData]);

  // Auto-save when toggle changes
  const handleToggle = async (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setSavingKey(key);
    
    try {
      const updatedSettings = { ...settings, [key]: value };
      
      // Build the notification settings object
      const notificationSettings = {
        // Customer notifications - Email
        confirmationEmailEnabled: true,
        reminderEmailEnabled: updatedSettings.reminderEnabled,
        reminderHoursBefore: 24,
        thankYouEmailEnabled: updatedSettings.thankYouEnabled,
        thankYouDelayHours: 24,
        thankYouIncludeReview: true,
        abandonedCartEmailEnabled: updatedSettings.abandonedCartEnabled,
        abandonedCartDelayMinutes: 30,
        // Customer notifications - SMS (high-impact only)
        confirmationSmsEnabled: updatedSettings.confirmationSmsEnabled,
        reminderSmsEnabled: updatedSettings.reminderSmsEnabled,
        cancellationSmsEnabled: updatedSettings.cancellationSmsEnabled,
        // Operator notifications
        newBookingEmailEnabled: updatedSettings.newBookingEnabled,
        newBookingSmsEnabled: false,
        newLeadEmailEnabled: updatedSettings.newLeadEnabled,
        newLeadSmsEnabled: false,
        newLeadDelayMinutes: 10,
        weeklyReportEmailEnabled: updatedSettings.weeklyReportEnabled,
        // Keep other settings at their defaults
        cancellationEmailEnabled: true,
        reviewRequestEmailEnabled: false,
        reviewRequestSmsEnabled: false,
      };
      
      await apiV2.patch('/auth/organizations/me', {
        // Send under settings.notificationSettings for proper backend storage
        settings: {
          notificationSettings: notificationSettings
        },
        // Also send top-level fields for backwards compatibility
        thankYouEmailEnabled: updatedSettings.thankYouEnabled,
        thankYouEmailDelayHours: 24,
        thankYouEmailIncludeReview: true,
      });

      mutate();
      
      // Show success toast for SMS toggles
      if (key === 'confirmationSmsEnabled' || key === 'reminderSmsEnabled' || key === 'cancellationSmsEnabled') {
        toast.success(value ? 'SMS notifications enabled' : 'SMS notifications disabled');
      }
    } catch (error) {
      setSettings(prev => ({ ...prev, [key]: !value }));
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSavingKey(null);
    }
  };

  // Simple notification row - Email only
  const NotificationRow = ({ icon: Icon, title, subtitle, settingKey, required = false }) => {
    const isEnabled = settings[settingKey];
    const isSaving = savingKey === settingKey;
    
    return (
      <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2 rounded-lg bg-gray-50 flex-shrink-0">
            <Icon className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900 text-sm">{title}</span>
              {required && (
                <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                  Required
                </span>
              )}
            </div>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs text-gray-500">Email</span>
          {isSaving && <Loader2 className="w-4 h-4 animate-spin text-blue-500" />}
          {required ? (
            <OnOffToggle value={true} onChange={() => {}} className="opacity-60" />
          ) : (
            <OnOffToggle 
              value={isEnabled} 
              onChange={(checked) => handleToggle(settingKey, checked)}
            />
          )}
        </div>
      </div>
    );
  };

  // Notification row with both Email and SMS toggles - all on the right
  const NotificationRowWithSms = ({ icon: Icon, title, subtitle, emailKey, smsKey, emailRequired = false }) => {
    const emailEnabled = settings[emailKey];
    const smsEnabled = settings[smsKey];
    const isSavingEmail = savingKey === emailKey;
    const isSavingSms = savingKey === smsKey;
    
    return (
      <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="p-2 rounded-lg bg-gray-50 flex-shrink-0">
            <Icon className="w-4 h-4 text-gray-500" />
          </div>
          <div className="min-w-0">
            <span className="font-medium text-gray-900 text-sm">{title}</span>
            {subtitle && (
              <p className="text-xs text-gray-400 mt-0.5">{subtitle}</p>
            )}
          </div>
        </div>
        
        {/* Email and SMS toggles on the right */}
        <div className="flex items-center gap-4 flex-shrink-0">
          {/* Email toggle */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 hidden sm:inline">Email</span>
            {isSavingEmail && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
            {emailRequired ? (
              <OnOffToggle value={true} onChange={() => {}} className="opacity-60" />
            ) : (
              <OnOffToggle 
                value={emailEnabled} 
                onChange={(checked) => handleToggle(emailKey, checked)}
              />
            )}
          </div>
          
          {/* SMS toggle */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <Phone className="w-3 h-3 text-green-600" />
              <span className="text-xs text-gray-500 hidden sm:inline">SMS</span>
            </div>
            {isSavingSms && <Loader2 className="w-3 h-3 animate-spin text-green-500" />}
            <OnOffToggle 
              value={smsEnabled} 
              onChange={(checked) => handleToggle(smsKey, checked)}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <TabPageLayout
      title="Notifications"
      subtitle="Control what emails and texts are sent"
      loading={isLoading}
    >
      <div className="space-y-6">
        
        {/* Customer Emails */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-6">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
            <Bell className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-900">Customer Notifications</h3>
          </div>
          
          <NotificationRowWithSms
            icon={CheckCircle}
            title="Booking Confirmation"
            subtitle="Sent immediately when booked"
            emailKey="confirmationEnabled"
            smsKey="confirmationSmsEnabled"
            emailRequired={true}
          />
          
          <NotificationRowWithSms
            icon={AlertTriangle}
            title="Cancellation Alert"
            subtitle="When booking is cancelled"
            emailKey="cancellationEnabled"
            smsKey="cancellationSmsEnabled"
            emailRequired={true}
          />
          
          <NotificationRow
            icon={FileText}
            title="Waiver Request"
            subtitle="Included with confirmation"
            settingKey="waiverEnabled"
            required={true}
          />
          
          <NotificationRowWithSms
            icon={Clock}
            title="Trip Reminder"
            subtitle="24 hours before"
            emailKey="reminderEnabled"
            smsKey="reminderSmsEnabled"
          />
          
          <NotificationRow
            icon={Star}
            title="Thank You"
            subtitle="24 hours after trip"
            settingKey="thankYouEnabled"
          />
          
          <NotificationRow
            icon={ShoppingCart}
            title="Abandoned Cart"
            subtitle="30 min after leaving checkout"
            settingKey="abandonedCartEnabled"
          />
        </div>

        {/* Your Alerts */}
        <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] p-6">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
            <Bell className="w-5 h-5 text-green-600" />
            <h3 className="font-semibold text-gray-900">Your Alerts</h3>
          </div>
          
          <NotificationRow
            icon={CheckCircle}
            title="New Booking"
            subtitle="Instant email when someone books"
            settingKey="newBookingEnabled"
          />
          
          <NotificationRow
            icon={UserPlus}
            title="New Lead"
            subtitle="10 min after someone signs up and doesn't checkout"
            settingKey="newLeadEnabled"
          />
          
          <NotificationRow
            icon={BarChart3}
            title="Weekly Report"
            subtitle="Performance summary every Monday"
            settingKey="weeklyReportEnabled"
          />
        </div>
        
      </div>
    </TabPageLayout>
  );
};

export default NotificationsTab;
